import { useEffect, useState } from "react";
import type { DashboardChartItem, DashboardKpiItem, DashboardLayout, DashboardSection, DashboardTableItem, DashboardTextItem } from "./api";
import { KpiCard } from "./cards/KpiCard";
import { ChartCard } from "./cards/ChartCard";
import { TableCard } from "./cards/TableCard";
import "./dashboard_designer.css";

export interface DashboardDataClient {
  kpiData: (org: string, id: string, body: { aggregation: string; column?: string | null; date_field?: string | null; dataset?: string | null }) => Promise<{ value: number | null }>;
  chartData: (org: string, id: string, body: {
    group_by: string;
    aggregation: string;
    column?: string | null;
    date_field?: string | null;
    start?: string | null;
    end?: string | null;
    stack_by?: string | null;
    query_mode?: string | null;
  }) => Promise<{
    rows: { label: string; value: number }[];
    categories?: string[];
    series?: { label: string; data: number[] }[];
    meta?: { actual?: number; target?: number; period_label?: string };
  }>;
  tableData: (org: string, id: string, body: { columns: string[] }) => Promise<{ rows: Record<string, unknown>[] }>;
}

export function DashboardRenderer({ orgId, dashboardId, layout, client }: {
  orgId: string;
  dashboardId: string;
  layout: DashboardLayout;
  client: DashboardDataClient;
}) {
  const sections = Array.isArray(layout?.sections) ? layout.sections : [];
  return (
    <div className="dashboard-renderer">
      <div className="dashboard-renderer__header">
        {layout?.eyebrow ? <div className="dashboard-renderer__eyebrow">{layout.eyebrow}</div> : null}
        <h1>{layout?.title || "Dashboard"}</h1>
      </div>
      {sections.length === 0 ? (
        <p className="dashboard-designer__empty-canvas">This dashboard has no widgets yet.</p>
      ) : sections.map(section => (
        <DashboardSectionView key={section.id} orgId={orgId} dashboardId={dashboardId} section={section} client={client} />
      ))}
    </div>
  );
}

function DashboardSectionView({ orgId, dashboardId, section, client }: {
  orgId: string; dashboardId: string; section: DashboardSection; client: DashboardDataClient;
}) {
  if (section.type === "text") {
    return (
      <section className="dashboard-section dashboard-section--text">
        {section.title ? <h2>{section.title}</h2> : null}
        {(section.items as DashboardTextItem[] | undefined ?? []).map(item => <p key={item.id}>{item.text}</p>)}
      </section>
    );
  }
  if (section.type === "kpi") {
    return (
      <section className="dashboard-section">
        {section.title ? <h2>{section.title}</h2> : null}
        <div className="dashboard-chart-row">
          {(section.items as DashboardKpiItem[] | undefined ?? []).map(item => (
            <div key={item.id} className="dashboard-chart-row__cell" style={{ gridColumn: `span ${item.width || 1}` }}>
              <KpiTile orgId={orgId} dashboardId={dashboardId} item={item} client={client} />
            </div>
          ))}
        </div>
      </section>
    );
  }
  if (section.type === "table") {
    return (
      <section className="dashboard-section">
        {section.title ? <h2>{section.title}</h2> : null}
        {(section.items as DashboardTableItem[] | undefined ?? []).map(item => (
          <TableTile key={item.id} orgId={orgId} dashboardId={dashboardId} item={item} client={client} />
        ))}
      </section>
    );
  }
  return (
    <section className="dashboard-section">
      {section.title ? <h2>{section.title}</h2> : null}
      <div className="dashboard-chart-row">
        {(section.items as DashboardChartItem[] | undefined ?? []).map(item => (
          <div key={item.id} className="dashboard-chart-row__cell"
            style={{ gridColumn: item.colStart ? `${item.colStart} / span ${item.colSpan || 3}` : `span ${item.colSpan || 3}` }}>
            <ChartTile orgId={orgId} dashboardId={dashboardId} item={item} client={client} />
          </div>
        ))}
      </div>
    </section>
  );
}

function KpiTile({ orgId, dashboardId, item, client }: { orgId: string; dashboardId: string; item: DashboardKpiItem; client: DashboardDataClient }) {
  const [value, setValue] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void client.kpiData(orgId, dashboardId, { aggregation: item.aggregation, column: item.column, date_field: item.dateField, dataset: item.dataset })
      .then(res => setValue(res.value))
      .catch(() => setValue(null))
      .finally(() => setLoading(false));
  }, [orgId, dashboardId, item.aggregation, item.column, item.dateField, item.dataset, client]);

  return <KpiCard item={item} value={value} loading={loading} />;
}

function ChartTile({ orgId, dashboardId, item, client }: { orgId: string; dashboardId: string; item: DashboardChartItem; client: DashboardDataClient }) {
  const [rows, setRows] = useState<{ label: string; value: number }[]>([]);
  const [categories, setCategories] = useState<string[] | undefined>();
  const [series, setSeries] = useState<{ label: string; data: number[] }[] | undefined>();
  const [meta, setMeta] = useState<{ actual?: number; target?: number; period_label?: string } | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void client.chartData(orgId, dashboardId, {
      group_by: item.groupBy,
      aggregation: item.aggregation,
      column: item.valueField ?? item.column,
      date_field: item.dateField,
      stack_by: item.stackBy,
      query_mode: item.queryMode,
    })
      .then(res => {
        setRows(res.rows);
        setCategories(res.categories);
        setSeries(res.series);
        setMeta(res.meta);
      })
      .catch(() => {
        setRows([]);
        setCategories(undefined);
        setSeries(undefined);
        setMeta(undefined);
      })
      .finally(() => setLoading(false));
  }, [orgId, dashboardId, item.groupBy, item.aggregation, item.column, item.valueField, item.dateField, item.stackBy, item.queryMode, client]);

  return <ChartCard item={item} rows={rows} categories={categories} series={series} meta={meta} loading={loading} />;
}

function TableTile({ orgId, dashboardId, item, client }: { orgId: string; dashboardId: string; item: DashboardTableItem; client: DashboardDataClient }) {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void client.tableData(orgId, dashboardId, { columns: item.columns })
      .then(res => setRows(res.rows))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [orgId, dashboardId, item.columns, client]);

  return <TableCard item={item} rows={rows} loading={loading} />;
}
